import React, { useState } from "react";
import {
  Modal,
  Table,
  Tag,
  Alert,
  Button,
  Space,
  Switch,
  Checkbox,
} from "antd";
import ChantButton from "../../../../../CommonComponents/ChantButton/ChantButton";
import {
  SyncOutlined,
  WarningFilled,
  CheckCircleFilled,
} from "@ant-design/icons";

const SyncPenyaNumbersModal = ({
  showInitialWarning,
  onContinueFromWarning,
  onCloseWarning,
  isOpen,
  onClose,
  allMembersWithNumbers,
  memberBreakdown,
  syncChanges,
  syncing,
  syncingNewMembers,
  showConfirm,
  setShowConfirm,
  handleSync,
}) => {
  const [showOnlyChanges, setShowOnlyChanges] = useState(false);
  const [downloadCsv, setDownloadCsv] = useState(true);

  const handleDownloadCsv = () => {
    const headers = ["Name", "Package", "Auto-Renew", "Current #", "New #"];
    const rows = allMembersWithNumbers.map((m) => [
      m.name,
      m.packageName || "",
      m.autoRenew ? "Yes" : "No",
      m.oldNumber,
      m.type === "remove" ? "Removed" : m.newNumber,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `member-numbers-sync-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleConfirm = () => {
    if (downloadCsv) handleDownloadCsv();
    handleSync();
  };

  const removeCount = syncChanges.filter((c) => c.type === "remove").length;
  const renumberCount = syncChanges.filter((c) => c.type === "renumber").length;
  const hasChanges = syncChanges.length > 0;

  const tableData = showOnlyChanges ? syncChanges : allMembersWithNumbers;

  const columns = [
    {
      title: "Member",
      dataIndex: "name",
      key: "name",
      render: (name) => <span style={{ fontWeight: 500 }}>{name}</span>,
    },
    {
      title: "Package",
      dataIndex: "packageName",
      key: "packageName",
      render: (pkg) =>
        pkg ? (
          <span style={{ color: "#555", fontSize: 13 }}>{pkg}</span>
        ) : (
          <span style={{ color: "#d1d5db", fontSize: 13 }}>—</span>
        ),
    },
    {
      title: "Auto-Renew",
      dataIndex: "autoRenew",
      key: "autoRenew",
      width: 110,
      align: "center",
      render: (val) =>
        val ? (
          <Tag color="green" style={{ margin: 0 }}>
            Active
          </Tag>
        ) : (
          <Tag color="default" style={{ margin: 0 }}>
            No
          </Tag>
        ),
    },
    {
      title: "Current #",
      dataIndex: "oldNumber",
      key: "oldNumber",
      width: 100,
      align: "center",
      render: (num) => (
        <span style={{ fontWeight: 700, color: "#374151" }}>{num}</span>
      ),
    },
    {
      title: "New #",
      key: "newNumber",
      width: 110,
      align: "center",
      render: (_, record) => {
        if (record.type === "unchanged") {
          return (
            <span style={{ fontWeight: 600, color: "#9ca3af" }}>
              {record.newNumber}
            </span>
          );
        }

        if (record.type === "remove") {
          return (
            <span style={{ fontWeight: 700, color: "#ef4444", fontSize: 15 }}>
              ✕
            </span>
          );
        }

        // renumber
        return (
          <span style={{ fontWeight: 700, color: "#1d4ed8" }}>
            {record.newNumber}
          </span>
        );
      },
    },
  ];

  const footer = syncingNewMembers ? (
    <div
      style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}
    >
      <span style={{ color: "#6b7280", fontSize: 13 }}>
        <SyncOutlined spin style={{ marginRight: 6 }} />
        Syncing new members...
      </span>
    </div>
  ) : hasChanges ? (
    showConfirm ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled style={{ color: "#d97706" }} />}
          message={
            <span style={{ fontWeight: 600, color: "#92400e" }}>
              This action cannot be undone
            </span>
          }
          description={
            <span style={{ color: "#78350f" }}>
              {removeCount > 0 && (
                <>
                  <strong>{removeCount}</strong> member
                  {removeCount !== 1 ? "s" : ""} will lose their group member
                  number.{" "}
                </>
              )}
              {renumberCount > 0 && (
                <>
                  <strong>{renumberCount}</strong> member
                  {renumberCount !== 1 ? "s" : ""} will be renumbered.{" "}
                </>
              )}
              If a mistake is made, numbers will need to be corrected manually.
            </span>
          }
          style={{
            backgroundColor: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 8,
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={() => setShowConfirm(false)} disabled={syncing}>
            Back
          </Button>
          <Button
            type="primary"
            danger
            loading={syncing}
            onClick={handleConfirm}
            icon={syncing ? null : <WarningFilled />}
          >
            {syncing ? "Updating Numbers..." : "Confirm Update"}
          </Button>
        </div>
      </div>
    ) : (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Checkbox
          checked={downloadCsv}
          onChange={(e) => setDownloadCsv(e.target.checked)}
        >
          Download CSV Report
        </Checkbox>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={onClose} disabled={syncing}>
            Cancel
          </Button>
          <ChantButton
            icon={<SyncOutlined />}
            onClick={() => setShowConfirm(true)}
          >
            Update Numbers
          </ChantButton>
        </div>
      </div>
    )
  ) : (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button onClick={onClose}>Close</Button>
    </div>
  );

  return (
    <>
      <Modal
        title={
          <Space>
            <WarningFilled style={{ color: "#d97706" }} />
            <span>Member Number Sync</span>
          </Space>
        }
        open={showInitialWarning}
        onCancel={onCloseWarning}
        centered
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={onCloseWarning}>Cancel</Button>
            <ChantButton onClick={onContinueFromWarning}>Continue</ChantButton>
          </div>
        }
      >
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled style={{ color: "#d97706" }} />}
          message="This feature will update your custom member numbers. It should only be used one time per season after your renewal window. It can not be undone."
          description={
            <span>
              Reach out to{" "}
              <a href="mailto:support@chant.fan">support@chant.fan</a> with
              questions.
            </span>
          }
          style={{
            backgroundColor: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 8,
            marginTop: 8,
          }}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <SyncOutlined style={{ color: "#2563eb" }} />
            <span>Sync Member Numbers</span>
          </Space>
        }
        open={isOpen}
        onCancel={onClose}
        width={820}
        centered
        closable={!syncing && !syncingNewMembers}
        maskClosable={!syncing && !syncingNewMembers}
        footer={footer}
      >
        <div style={{ padding: "8px 0" }}>
          {memberBreakdown && !syncingNewMembers && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 14,
                fontSize: 13,
                color: "#374151",
              }}
            >
              {[
                {
                  label: "active, numbered",
                  count: memberBreakdown.activeNumbered,
                  bg: "#f0fdf4",
                  border: "#bbf7d0",
                  color: "#15803d",
                },
                {
                  label: "active, no number",
                  count: memberBreakdown.activeNoNumber,
                  bg: "#fffbeb",
                  border: "#fcd34d",
                  color: "#b45309",
                },
                {
                  label: "expired, numbered",
                  count: memberBreakdown.lapsedNumbered,
                  bg: "#fef2f2",
                  border: "#fecaca",
                  color: "#dc2626",
                },
                {
                  label: "expired, no number",
                  count: memberBreakdown.lapsedNoNumber,
                },
                { label: "locked", count: memberBreakdown.locked },
                { label: "fans", count: memberBreakdown.fans },
                { label: "no package", count: memberBreakdown.noPackage },
              ]
                .filter((c) => c.count > 0)
                .map((c, i) => (
                  <React.Fragment key={c.label}>
                    {i > 0 && <span style={{ color: "#9ca3af" }}>+</span>}
                    <span
                      style={{
                        padding: "3px 10px",
                        borderRadius: 999,
                        background: c.bg || "#f3f4f6",
                        border: `1px solid ${c.border || "#e5e7eb"}`,
                        color: c.color || "#374151",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <strong>{c.count}</strong> {c.label}
                    </span>
                  </React.Fragment>
                ))}
              <span style={{ color: "#9ca3af" }}>=</span>
              <span
                style={{
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: "#111827",
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                <strong>{memberBreakdown.total}</strong> members
              </span>
            </div>
          )}
          {syncingNewMembers ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "48px 24px",
                gap: 12,
              }}
            >
              <SyncOutlined spin style={{ fontSize: 32, color: "#2563eb" }} />
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#1e40af",
                  margin: 0,
                }}
              >
                Syncing new members...
              </p>
              <p style={{ color: "#6b7280", margin: 0, textAlign: "center" }}>
                Assigning member numbers to newly joined or renewed members
                before generating the preview.
              </p>
            </div>
          ) : allMembersWithNumbers.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "48px 24px",
                gap: 12,
              }}
            >
              <CheckCircleFilled style={{ fontSize: 48, color: "#22c55e" }} />
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#15803d",
                  margin: 0,
                }}
              >
                No members with a group member number
              </p>
              <p style={{ color: "#6b7280", margin: 0, textAlign: "center" }}>
                No members have been assigned a group member number yet.
              </p>
            </div>
          ) : (
            <>
              {/* Summary badges + toggle row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 14,
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <div
                    style={{
                      padding: "4px 10px",
                      background: "#f3f4f6",
                      borderRadius: 6,
                      fontSize: 13,
                      color: "#374151",
                    }}
                  >
                    <strong>{allMembersWithNumbers.length}</strong> numbered
                  </div>
                  {removeCount > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "4px 10px",
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 6,
                      }}
                    >
                      <div
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: "#ef4444",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 13, color: "#991b1b" }}>
                        <strong>{removeCount}</strong> losing number
                      </span>
                    </div>
                  )}
                  {renumberCount > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "4px 10px",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: 6,
                      }}
                    >
                      <div
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: "#3b82f6",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 13, color: "#1e40af" }}>
                        <strong>{renumberCount}</strong> renumbered
                      </span>
                    </div>
                  )}
                  {!hasChanges && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "4px 10px",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: 6,
                      }}
                    >
                      <CheckCircleFilled
                        style={{ color: "#22c55e", fontSize: 12 }}
                      />
                      <span style={{ fontSize: 13, color: "#15803d" }}>
                        No changes needed
                      </span>
                    </div>
                  )}
                </div>

                {hasChanges && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontSize: 13, color: "#6b7280" }}>
                      Show only changes
                    </span>
                    <Switch
                      size="small"
                      checked={showOnlyChanges}
                      onChange={setShowOnlyChanges}
                    />
                  </div>
                )}
              </div>

              <Table
                columns={columns}
                dataSource={tableData}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ y: 340 }}
                rowClassName={(record) => {
                  if (record.type === "remove") return "sync-row-remove";
                  if (record.type === "renumber") return "sync-row-renumber";
                  return "";
                }}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              />
            </>
          )}
        </div>

        <style>{`
        .sync-row-remove td {
          background-color: #fff5f5 !important;
        }
        .sync-row-renumber td {
          background-color: #f5f8ff !important;
        }
        .sync-row-remove:hover td,
        .sync-row-renumber:hover td {
          filter: brightness(0.97);
        }
      `}</style>
      </Modal>
    </>
  );
};

export default SyncPenyaNumbersModal;
